import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import InputField from '@components/InputField';

import TableActionDropdown from '@features/design/components/TableActionDropdown';
import {
	canEditBrandOrFactory,
	canEditCountry,
	canEditDropdown,
	canEditLocation,
	canEditQcApproval,
	canEditRemarks,
	canEditSampleId,
	canShowActions,
	getSampleTypeBasedOnConditions,
	isAllowedToEditInventory,
	isCopyedStyle,
	isFactory,
	locationInfo,
	SAMPLE_STATUS,
	SAMPLE_TABLE_HEADERS,
	SAMPLE_TYPES
} from '@features/design/utils/designUtils';

import { setSystemUserPreferences } from '@services/commonService';

import { convertDateToLocal, getUserPreference, onGetLocalFromUtc, replaceUnderLine } from '@utils/Util';

const AllSampleAccordion = ({
	isDesignEdit,
	onChangeSampleInputField,
	onSelectOptions,
	onAddNewRow,
	onWrongEntry,
	allSampleList = [],
	selectedRowId,
	setSelectedRowId,
	countryList,
	suppliers,
	brands,
	errors,
	fetchSuppliers,
	getBrands,
	fetchSampleLocations,
	locationsList,
	onSelectSampleLocation,
	parentId = null,
	isNetexDesign,
	designDetails
}) => {
	const dispatch = useDispatch();
	const findError = (field, sample) => {
		return errors.find((error) => error.rowId === sample?.rowId && error[field]);
	};
	const userPreference = getUserPreference();
	const [isOpenAccordion, setIsOpenAccordion] = useState(userPreference?.IS_TECHPACK_ACCORDION_OPEN);
	const [focusedRow, setFocusedRow] = useState(0); // Track the focused row index

	const handleUserPreferenceUpdate = async (isTechPackOpen) => {
		const userPreference = getUserPreference();

		const body = {
			...userPreference,
			IS_TECHPACK_ACCORDION_OPEN: isTechPackOpen
		};
		await setSystemUserPreferences(body);
	};

	const toggleAccordion = () => {
		handleUserPreferenceUpdate(!isOpenAccordion);
		setIsOpenAccordion((prev) => !prev);
	};

	useEffect(() => {
		const inputField = document.getElementById(`sampleId-${focusedRow}`);
		if (inputField) {
			inputField.focus();
		}
	}, [focusedRow]);

	const handleKeyDown = (e, index) => {
		if (e.key === 'Enter') {
			e.preventDefault();

			if (index + 1 < allSampleList.length) {
				setFocusedRow(index + 1);
			} else {
				setFocusedRow(0);
			}
		}
	};

	const renderTableBody = () => {
		return (
			<React.Fragment>
				{allSampleList.map((sample, index) => (
					<tr
						key={`${index}-${sample?.id}`}
						className={`${
							(sample?.isWrongEntry && isDesignEdit) || isCopyedStyle(parentId, sample)
								? 'dissable'
								: ''
						}`}
					>
						<td className={`sample-td ${findError('sampleId', sample) ? 'error-input' : ''}`}>
							{canEditSampleId(sample, isDesignEdit, parentId) ? (
								<InputField
									type="number"
									value={sample?.sampleId || ''}
									onChangeHandler={(e) => onChangeSampleInputField(e, sample?.rowId)}
									name="sampleId"
									id={`sampleId-${index}`}
									placeholder="Sample Id"
									onKeyDown={(e) => handleKeyDown(e, index)}
									autoFocus={focusedRow === index}
								/>
							) : (
								<p className={`p-0 ${sample?.isWrongEntry ? 'regular-14 gray_dark_02' : ''}`}>
									{sample?.isWrongEntry ? (
										<span>
											<del>{sample?.sampleId}</del>
										</span>
									) : (
										<span>{sample?.sampleId}</span>
									)}
									{sample?.isWrongEntry && (
										<span className="regular-14 red"> (Wrong Entry)</span>
									)}
									{!sample?.isWrongEntry && isCopyedStyle(parentId, sample) && (
										<span className="regular-14 gray_dark_02"> (Copy)</span>
									)}
								</p>
							)}
						</td>
						<td
							className={`${
								canEditCountry(sample, isDesignEdit, parentId, isNetexDesign)
									? 'country'
									: 'country dissable-td'
							}`}
						>
							{canEditCountry(sample, isDesignEdit, parentId, isNetexDesign) ? (
								<CustomDropdown
									isShowSearch={false}
									title="Select"
									items={countryList}
									selectedItem={sample?.countryResponse?.code}
									onItemClick={(value) =>
										onSelectOptions(value, 'countryResponse', sample?.rowId, true)
									}
								/>
							) : (
								<p>{sample?.countryResponse?.code}</p>
							)}
						</td>
						<td className="qc-date">
							{canEditQcApproval(isDesignEdit, sample, parentId) ? (
								<InputField
									type="date"
									value={sample?.qcApprovalDate || ''}
									onChangeHandler={(e) => onChangeSampleInputField(e, sample?.rowId)}
									id="qcApprovalDate"
									name="qcApprovalDate"
								/>
							) : (
								<p>
									{sample?.qcApprovalDate
										? onGetLocalFromUtc(sample?.qcApprovalDate, 'DD MMM, YYYY')
										: ''}
								</p>
							)}
						</td>
						<td className="type-column">
							{canEditDropdown(sample, isDesignEdit, parentId) ? (
								<CustomDropdown
									title="Select"
									items={SAMPLE_TYPES}
									selectedItem={getSampleTypeBasedOnConditions(sample)}
									onItemClick={(value) =>
										onSelectOptions(value?.id, 'sampleType', sample?.rowId)
									}
								/>
							) : (
								<p>{replaceUnderLine(sample?.sampleType)}</p>
							)}
						</td>
						<td className={`${canEditBrandOrFactory(sample, parentId) ? '' : 'dissable-td'}`}>
							{isDesignEdit && canEditBrandOrFactory(sample, parentId) ? (
								<CustomDropdown
									title="Select"
									onItemSearch={(value) =>
										isFactory(sample?.sampleType)
											? fetchSuppliers(value)
											: dispatch(getBrands(`?page=0&size=1000&name=${value}`))
									}
									items={isFactory(sample?.sampleType) ? suppliers : brands}
									selectedItem={
										isFactory(sample?.sampleType)
											? sample?.supplierResponse?.name
											: sample?.brandResponse?.name
									}
									onItemClick={(value) =>
										onSelectOptions(
											value,
											isFactory(sample?.sampleType)
												? 'supplierResponse'
												: 'brandResponse',
											sample?.rowId,
											true
										)
									}
								/>
							) : (
								<p>{sample?.supplierResponse?.name || sample?.brandResponse?.name}</p>
							)}
						</td>
						<td className="color">
							{canEditDropdown(sample, isDesignEdit, parentId) ? (
								<InputField
									type="text"
									value={sample?.color || ''}
									onChangeHandler={(e) => onChangeSampleInputField(e, sample?.rowId)}
									id="color"
									name="color"
								/>
							) : (
								<p>{sample?.color}</p>
							)}
						</td>
						<td className="size">
							{canEditDropdown(sample, isDesignEdit, parentId) ? (
								<InputField
									type="text"
									value={sample?.size || sample?.baseSizeMap?.NITEX_DEVELOPMENT || ''}
									onChangeHandler={(e) => onChangeSampleInputField(e, sample?.rowId)}
									id="size"
									name="size"
								/>
							) : (
								<p>{sample?.size || sample?.baseSizeMap?.NITEX_DEVELOPMENT}</p>
							)}
						</td>
						<td className="location-colum">
							{canEditLocation(sample, isDesignEdit, parentId) ? (
								<CustomDropdown
									title="Select"
									items={locationsList}
									onAddNewButtonClick={(value, setIsOpen) => {
										onSelectSampleLocation(value, sample?.rowId);
										setIsOpen(false);
									}}
									onItemSearch={(value) => {
										fetchSampleLocations(value);
									}}
									onEdit={(value) =>
										onSelectSampleLocation({ ...value, isEdit: true }, sample?.rowId)
									}
									selectedItem={locationInfo({
										...sample?.sampleInventoryLocationResponse,
										sl: sample?.sl
									})}
									isEditable={true}
									showAddNewButton
									onItemClick={(value) =>
										onSelectOptions(
											value,
											'sampleInventoryLocationResponse',
											sample?.rowId
										)
									}
								/>
							) : (
								<p>
									{locationInfo({
										...sample?.sampleInventoryLocationResponse,
										sl: sample?.sl
									})}
								</p>
							)}
						</td>
						<td>
							{sample?.sampleInventoryLocationAddedBy?.name && (
								<p className="regular-14 gray_dark_02">
									{sample?.sampleInventoryLocationAddedBy?.nickName}
								</p>
							)}
							{sample?.sampleInventoryLocationAddedAt && (
								<p className="regular-12 gray_dark_03">
									{convertDateToLocal(
										sample?.sampleInventoryLocationAddedAt,
										'DD MMM, YYYY'
									)}
								</p>
							)}
						</td>
						<td className="remark-td">
							{canEditRemarks(sample, isDesignEdit, parentId) ? (
								<InputField
									type="text"
									value={sample?.remarks || ''}
									onChangeHandler={(e) => onChangeSampleInputField(e, sample?.rowId)}
									id="remarks"
									name="remarks"
								/>
							) : (
								<p>{sample?.remarks}</p>
							)}
						</td>
						<td>
							{canEditDropdown(sample, isDesignEdit, parentId) ? (
								<CustomDropdown
									title="Select"
									items={SAMPLE_STATUS}
									selectedItem={replaceUnderLine(sample?.sampleStatus)}
									onItemClick={(value) =>
										onSelectOptions(value?.id, 'sampleStatus', sample?.rowId)
									}
								/>
							) : (
								<p>{replaceUnderLine(sample?.sampleStatus)}</p>
							)}
						</td>

						<td
							className={`${
								isCopyedStyle(parentId, sample)
									? 'cursor-pointer action-column dissable'
									: 'cursor-pointer action-column '
							}`}
						>
							{sample?.sampleId && canShowActions(sample, isDesignEdit, parentId) && (
								<TableActionDropdown
									rowId={sample?.rowId}
									selectedRowId={selectedRowId}
									setSelectedRowId={setSelectedRowId}
									onWrongEntry={onWrongEntry}
									sampleDetails={sample}
								/>
							)}
						</td>
					</tr>
				))}
				{isDesignEdit && isAllowedToEditInventory() && (
					<tr>
						<td
							className="brand-column cursor-pointer regular-14 gray_dark_01 "
							onClick={onAddNewRow}
							colSpan="12"
						>
							<span>
								<img
									src={`${process.env.PUBLIC_URL}/icons/plus-20.svg`}
									alt="svg"
									width="20px"
								/>
							</span>
							Add
						</td>
					</tr>
				)}
			</React.Fragment>
		);
	};

	return (
		<React.Fragment>
			<div className="style-sample-table">
				<div className="sample-accordion">
					<div className="accordion" id="accordionExample">
						<div className="card">
							<div className="card-header" id="headingOne">
								<h2 className="mb-0">
									<button
										className={`btn btn-link btn-block d-flex justify-content-between text-left gray_dark_01 regular-14 weight-600 p-0 ${
											isOpenAccordion ? '' : 'collapsed'
										}`}
										type="button"
										onClick={() => toggleAccordion()}
										aria-expanded={isOpenAccordion}
										aria-controls="collapseOne"
									>
										<p>
											All SAMPLES
											{parentId !== allSampleList[0]?.productId ? (
												<span
													className="blue regular-12 weight-500 ml-12"
													onClick={(e) => {
														e.stopPropagation();
														window.open(
															`/collections/list?openDesign=true&productId=${designDetails?.parentProductId}`,
															'_blank'
														);
													}}
												>
													Original Style
													<img
														src={`${process.env.PUBLIC_URL}/icons/arrow-forward-blue.svg`}
														alt=""
													/>
												</span>
											) : null}
										</p>
										<div className="arrow-down">
											<img
												src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
												alt="arrow"
											/>
										</div>
									</button>
								</h2>
							</div>

							<div
								id="collapseOne"
								className={`collapse scroll-x-label ${isOpenAccordion ? 'show' : ''}`}
								aria-labelledby="headingOne"
								data-parent="#accordionExample"
							>
								<div className="card-body p-0 border-0">
									<div className="sample-table ">
										<table className="table with-border no-hover height-sm hover  ">
											<thead>
												<tr>
													{SAMPLE_TABLE_HEADERS.map((col, index) => (
														<th key={`${index}-${col}`}>{col}</th>
													))}
													{isDesignEdit && <th></th>}
												</tr>
											</thead>
											<tbody>{renderTableBody()}</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default AllSampleAccordion;
